import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

export interface ErrorFallbackProps {
  /** When provided (e.g. from ErrorBoundary), shows the actual error for debugging */
  error?: Error | null;
  componentStack?: string | null;
}

export function ErrorFallback({ error, componentStack }: ErrorFallbackProps = {}) {
  const handleRefresh = () => {
    window.location.reload();
  };
  const showDetails = (import.meta.env.DEV || import.meta.env.MODE === 'development') && error;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 brushed-steel vignette-deep">
      <Card className="max-w-md w-full machined-edge bg-pro-gunmetal/80 border-pro-gunmetal">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mb-4 border border-amber-500/30 animate-stadium-pulse">
            <AlertTriangle className="w-8 h-8 text-amber-500" />
          </div>
          <CardTitle className="text-2xl text-white font-broadcast">SOMETHING WENT WRONG</CardTitle>
          <CardDescription className="text-base mt-2 text-white/70">
            {showDetails
              ? error!.message
              : 'The app encountered an unexpected error. Our team has been notified.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center text-sm text-white/60">
          {showDetails && error?.stack ? (
            <>
              <details className="text-left mt-4">
                <summary className="cursor-pointer text-amber-400 hover:text-amber-300">
                  Stack trace
                </summary>
                <pre className="mt-2 p-2 bg-black/40 rounded text-xs overflow-auto max-h-40 font-mono">
                  {error.stack}
                </pre>
                {componentStack && (
                  <pre className="mt-2 p-2 bg-black/40 rounded text-xs overflow-auto max-h-24 font-mono">
                    {componentStack}
                  </pre>
                )}
              </details>
              <p className="mt-4">
                Try refreshing the page. If the problem persists, please contact support.
              </p>
            </>
          ) : (
            <p>Try refreshing the page. If the problem persists, please contact support.</p>
          )}
        </CardContent>
        <CardFooter className="flex justify-center">
          <Button
            variant="outline"
            onClick={handleRefresh}
            className="border-pro-gunmetal text-white hover:bg-pro-gunmetal"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reload
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
